import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PushNotificationContext = createContext(null);

export const usePushNotifications = () => {
  const context = useContext(PushNotificationContext);
  if (!context) {
    throw new Error('usePushNotifications must be used within PushNotificationProvider');
  }
  return context;
};

// Convert VAPID key from base64 to Uint8Array
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/');

  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

export const PushNotificationProvider = ({ children }) => {
  const [isSupported, setIsSupported] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [permission, setPermission] = useState('default');
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);

  // Check if push notifications are supported
  useEffect(() => {
    const supported = 'serviceWorker' in navigator && 'PushManager' in window;
    setIsSupported(supported);
    
    if (supported) {
      setPermission(Notification.permission);
      checkSubscription();
    } else {
      setLoading(false);
    }
  }, []);

  // Check current subscription status
  const checkSubscription = useCallback(async () => {
    try {
      const registration = await navigator.serviceWorker.ready;
      const sub = await registration.pushManager.getSubscription();
      
      setSubscription(sub);
      setIsSubscribed(!!sub);
    } catch (error) {
      console.error('Error checking subscription:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  // Register service worker
  const registerServiceWorker = useCallback(async () => {
    if (!('serviceWorker' in navigator)) {
      throw new Error('Service Worker not supported');
    }

    try {
      const registration = await navigator.serviceWorker.register('/sw.js');
      console.log('Service Worker registered:', registration);
      return registration;
    } catch (error) {
      console.error('Service Worker registration failed:', error);
      throw error;
    }
  }, []);

  // Request notification permission
  const requestPermission = useCallback(async () => {
    if (!isSupported) {
      throw new Error('Push notifications not supported');
    }

    const result = await Notification.requestPermission();
    setPermission(result);
    return result;
  }, [isSupported]);

  // Subscribe to push notifications
  const subscribe = useCallback(async (outletIds = []) => {
    if (!isSupported) {
      throw new Error('Push notifications not supported');
    }

    try {
      // Request permission if not granted
      if (permission !== 'granted') {
        const result = await requestPermission();
        if (result !== 'granted') {
          throw new Error('Notification permission denied');
        }
      }

      // Register service worker
      await registerServiceWorker();
      
      // Get VAPID public key from server
      const vapidResponse = await axios.get(`${API}/push/vapid-key`);
      const vapidPublicKey = vapidResponse.data.publicKey;

      // Subscribe to push
      const registration = await navigator.serviceWorker.ready;
      const sub = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidPublicKey)
      });

      // Send subscription to server
      const token = localStorage.getItem('token');
      await axios.post(`${API}/push/subscribe`, {
        subscription: {
          endpoint: sub.endpoint,
          keys: {
            p256dh: btoa(String.fromCharCode.apply(null, new Uint8Array(sub.getKey('p256dh')))),
            auth: btoa(String.fromCharCode.apply(null, new Uint8Array(sub.getKey('auth'))))
          }
        },
        outlet_ids: outletIds
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setSubscription(sub);
      setIsSubscribed(true);

      return sub;
    } catch (error) {
      console.error('Subscription failed:', error);
      throw error;
    }
  }, [isSupported, permission, requestPermission, registerServiceWorker]);

  // Unsubscribe from push notifications
  const unsubscribe = useCallback(async () => {
    if (!subscription) return;

    try {
      // Unsubscribe from push
      await subscription.unsubscribe();

      // Remove from server
      const token = localStorage.getItem('token');
      await axios.post(`${API}/push/unsubscribe`, null, {
        params: { endpoint: subscription.endpoint },
        headers: { Authorization: `Bearer ${token}` }
      });

      setSubscription(null);
      setIsSubscribed(false);
    } catch (error) {
      console.error('Unsubscribe failed:', error);
      throw error;
    }
  }, [subscription]);

  // Send test notification
  const sendTestNotification = useCallback(async () => {
    const token = localStorage.getItem('token');
    await axios.post(`${API}/push/test`, {}, {
      headers: { Authorization: `Bearer ${token}` }
    });
  }, []);

  const value = {
    isSupported,
    isSubscribed,
    permission,
    subscription,
    loading,
    requestPermission,
    subscribe,
    unsubscribe,
    sendTestNotification,
    registerServiceWorker
  };

  return (
    <PushNotificationContext.Provider value={value}>
      {children}
    </PushNotificationContext.Provider>
  );
};
